import type { Product } from '@catalyst-psa/shared';
import { PSA_CLASS_LABELS } from '@catalyst-psa/shared';

/**
 * Product label for dropdowns and selected-product display:
 * "SKU — Customer description — Service category" (empty parts are omitted).
 */
export function formatProductLabel(
  product: Pick<Product, 'sku' | 'customerDescription' | 'serviceCategoryName'>,
): string {
  return [product.sku, product.customerDescription, product.serviceCategoryName]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(' — ');
}

/** Fields a product picker's single search box matches against. */
function productSearchHaystack(product: Product): string {
  return [
    product.sku,
    product.name,
    product.psaClass ? PSA_CLASS_LABELS[product.psaClass] : null,
    product.psaClass,
    product.customerDescription,
    product.serviceCategoryName,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase();
}

/**
 * Filters the catalog by a free-text query matched against SKU, name, PSA class,
 * customer description and service category at once. Every whitespace-separated term
 * must match, so "func imp" narrows the same way a user expects.
 */
export function filterProducts(products: Product[], query: string): Product[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return products;
  return products.filter((product) => {
    const haystack = productSearchHaystack(product);
    return terms.every((term) => haystack.includes(term));
  });
}

